'use client';

import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { API_WRITE_EVENT } from '@/lib/api-read-cache';

/**
 * TanStack Query Provider
 *
 * Provides QueryClient for server state management across the app.
 *
 * Default Configuration:
 * - staleTime: 5 minutes (data considered fresh)
 * - gcTime: 30 minutes (garbage collection time, formerly cacheTime)
 * - retry: 1 (single retry on failure)
 * - refetchOnWindowFocus: false (don't auto-refetch on window focus)
 */
export function QueryProvider({ children }: { children: React.ReactNode }) {
  const [queryClient] = useState(
    createAppQueryClient
  );

  // Any successful write anywhere in the app marks every cached query stale
  // and refetches the ones on screen. Debounced so a burst of writes (a save
  // that PUTs several resources) triggers one refresh.
  useEffect(() => {
    let timer: ReturnType<typeof setTimeout> | undefined;
    const onWrite = () => {
      clearTimeout(timer);
      timer = setTimeout(() => { void queryClient.invalidateQueries({ refetchType: 'active' }); }, 300);
    };
    window.addEventListener(API_WRITE_EVENT, onWrite);
    return () => { clearTimeout(timer); window.removeEventListener(API_WRITE_EVENT, onWrite); };
  }, [queryClient]);

  return (
    <QueryClientProvider client={queryClient}>
      {children}
    </QueryClientProvider>
  );
}

/**
 * Create the application QueryClient.
 *
 * Mutations deliberately do not retry by default. UZO mutations include
 * non-idempotent operations such as creating resources and starting runs; an
 * automatic retry after a lost response can duplicate those operations. A
 * specific mutation may opt in only when its endpoint has an idempotency key
 * or is otherwise proven safe to repeat.
 */
export function createAppQueryClient(): QueryClient {
  return new QueryClient({
    defaultOptions: {
      queries: {
        staleTime: 5 * 60 * 1000,
        gcTime: 30 * 60 * 1000,
        retry: 1,
        refetchOnWindowFocus: false,
        refetchOnMount: false,
      },
      mutations: {
        retry: 0,
      },
    },
  });
}
